/**
 * HTTP-backed API client surface, matching the `mockApi` shape 1:1.
 *
 * Components import `api` and the transport decides where calls land:
 *   - In tests / when `VITE_USE_MSW=1`, MSW intercepts the fetches and serves
 *     from the in-memory store (`apps/web/src/test/mockBackend.ts`).
 *   - Otherwise, fetches hit the real backend via the same-origin path
 *     or the cross-origin `VITE_API_BASE` URL.
 *
 * This file is the per-resource method registry. Supporting infrastructure
 * lives in two adjacent files:
 *   - `apiClient.ts` — fetch wrapper, JSON/error handling, helper verbs.
 *   - `liveBus.ts`   — WebSocket subscriber for live events.
 *
 * Project view prefs stay local here because the backend doesn't persist
 * them in v1 (BACKEND_PRD §2.9). When the prefs endpoints land, swap the
 * `getPrefs`/`setPrefs` impls to `fetch` calls — caller surface stays
 * the same.
 */

import type {
  Column, Comment, Company, Invite, Project, Ticket, TicketFix, TicketSummary, User,
  ApiToken, ProjectViewPrefs, LiveEvent, UserTheme,
  ProjectWidget, UpdateProjectWidgetBody, WidgetVerification, WidgetOverviewItem,
  TicketType, TicketTypeRef, ProjectTicketType, NarrationAssignmentsResponse, TourProgress, TourWriter, BulkMoveResult,
} from '@vd/types';
import { withColumnTones } from '@vd/types';
import { request, requestRaw, get, post, patch } from './apiClient';
import { subscribe as liveSubscribe } from './liveBus';
import { readRememberedViewPrefs, rememberViewPrefs } from './viewPrefsStorage';

/** A widget report's earlier reports from the same tab, as `GET /tickets/:id/timeline` gives them (apps/api/src/earlierReports.ts). */
export interface EarlierReports {
  /** How many earlier reports the trail names. */
  count: number;
  /** Those this person can open, newest first. */
  tickets: Array<{ id: string; createdAt: string }>;
  /** The lookup ran out of time: counted, none listed. */
  unresolved?: true;
}

/** A ticket as a Dashboard section lists it (TOADO-720). */
/** The period a report covers and the one it is compared with, as instants (apps/api/src/reports.ts). */
export type ReportPeriod = { kind: string; timeZone: string; from: string; to: string; previousFrom: string; previousTo: string };
/** Tickets opened in the period against the previous one (TOADO-733). */
export type ReportOverview = {
  period: ReportPeriod;
  opened: { current: number; previous: number };
  byProject: Array<{ project: { id: string; name: string; color: string }; opened: { current: number; previous: number } }>;
};

type ReportProject = { id: string; name: string; color: string };
type ReportTicket = { id: string; title: string; createdAt: string };
/** The captured errors in the most tickets (TOADO-734). */
export type ReportErrors = {
  period: ReportPeriod;
  coverage: { tickets: number; captured: number; withErrors: { current: number; previous: number } };
  truncated: boolean;
  groups: Array<{ key: string; example: string; ticketCount: number; projects: ReportProject[]; firstSeen: string; lastSeen: string; tickets: ReportTicket[] }>;
};
/** The pages that drew the most tickets (TOADO-735). */
export type ReportPages = {
  period: ReportPeriod;
  truncated: boolean;
  pages: Array<{ page: string; current: number; previous: number; tickets: ReportTicket[] }>;
  noPage: { current: number; previous: number };
  /** The same tickets by project, for the Tickets page's tiles (16 Oct). */
  byProject: Array<{
    project: { id: string; name: string; color: string };
    current: number; previous: number; bugs: number; pageCount: number;
    topPages: Array<{ page: string; tickets: number }>;
  }>;
};

/** Opened against closed per week, with the backlog (TOADO-736); closes count from `countedFrom`. */
export type ReportTrend = {
  period: ReportPeriod;
  countedFrom: string | null;
  /** `backlog` is null for a week that ended before closes were counted. */
  weeks: Array<{ week: string; starts: string; ends: string; opened: number; closed: number; backlog: number | null }>;
};
type FixStats = { closed: number; medianSeconds: number | null; p90Seconds: number | null };
/** Time from capture to the last column, per project (TOADO-737). */
export type ReportTimeToFix = {
  period: ReportPeriod;
  countedFrom: string | null;
  overall: { current: FixStats; previous: FixStats };
  byProject: Array<{ project: ReportProject; current: FixStats; previous: FixStats; slowest: Array<{ id: string; title: string; seconds: number; closedAt: string }> }>;
};

/** What each person and each AI agent did over the period, for owners (TOADO-738). */
export type ReportTeam = {
  period: ReportPeriod;
  /** `agents`: the AI agents that worked here through the person's connections (16 Oct). */
  people: Array<{
    userId: string; name: string; role: string; opened: number; closed: number; comments: number; activeDays: number; lastActiveAt: string | null; agents: string[];
    /** What they used (16 Oct): tickets by how they were filed, walkthroughs, recording and narration in the period, storage held now. */
    uses: {
      filed: { pins: number; edits: number; markup: number; screenshots: number; walkthrough: number; byHand: number; agent: number };
      walkthroughs: number; recordingSeconds: number; narrationSeconds: number; storageBytes: number;
    };
  }>;
  agents: Array<{ agent: string; people: number; opened: number; comments: number; moves: number; fixes: number }>;
};

/** How a person filed a ticket (TOADO-862); a visitor's report is no one's. */
export type FiledBy = 'extension' | 'walkthrough' | 'teamWidget' | 'manual' | 'agent';
export type ReportPeople = {
  period: ReportPeriod;
  people: Array<{
    userId: string; name: string; role: string;
    opened: Record<FiledBy, number>; openedTotal: number; closed: number; carrying: number; sentToJira: number;
    /** Tickets that named them in the period: a description, or a comment written then (TOADO-965). Missing from an API before it. */
    mentioned?: number;
  }>;
  fromVisitors: number;
  jiraCountedFrom: string | null;
  /** From when closes are counted: when moves were first recorded. */
  closesCountedFrom: string | null;
};

/** How a visitor reported (apps/api/src/reportCollection.ts). */
export type CollectionKind = 'form' | 'pin' | 'recording';
/** Bug collection (TOADO-963): what the bug collection widget brought in. */
export type ReportCollection = {
  period: ReportPeriod;
  truncated: boolean;
  opened: { current: number; previous: number; byKind: Record<CollectionKind, number> };
  widgets: Array<{
    project: { id: string; name: string; color: string };
    opened: { current: number; previous: number };
    byKind: Record<CollectionKind, number>;
    sites: Array<{ host: string; reports: number }>;
  }>;
  pages: Array<{ host: string; path: string; reports: number }>;
  visitors: {
    /** The history the rule reads was cut: in all and returning are at least what is shown. */
    historyTruncated: boolean;
    inPeriod: number; named: number; returning: number;
    /** Reports in the period no visitor can be named for: from a shared network, or with neither email nor address. */
    unnamedReports: number;
    list: Array<{
      key: string; named: boolean; name: string | null; email: string | null;
      current: number; total: number; open: number; returning: boolean; widgets: number; firstAt: string; lastAt: string;
    }>;
  };
  outcomes: { intake: number; board: number; closed: number; archived: number; medianHoursToClose: number | null };
  /** Owners only; null for anyone else. */
  recording: null | {
    /** The month the minutes are for, as YYYY-MM-01: the current month, whatever period is chosen. */
    month: string;
    widgets: Array<{
      project: { id: string; name: string; color: string };
      switchedOn: boolean; capSeconds: number; usedSeconds: number; reservedSeconds: number; refused: number; sent: number; narrated: number;
    }>;
  };
};

/** What the icon routes answer with: the icon fields of a project. */
export type ProjectIconAnswer = Pick<Project, 'iconUrl' | 'iconChoice' | 'hasUploadedIcon'>;

export type TicketTypeKey = 'bug' | 'improvement' | 'feature' | 'other' | 'untyped';
export type ReportPatterns = {
  period: ReportPeriod;
  truncated: boolean;
  sameElement: Array<{
    page: string; element: { kind: 'testId' | 'component' | 'text'; label: string };
    tickets: number; people: number; examples: Array<{ id: string; title: string }>;
    /** The projects it was reported in, most first (16 Oct). */
    projects: Array<{ id: string; name: string; color: string }>;
  }>;
  /** `byWeek`: tickets in each of the eight weeks, oldest first, ending with the period's (16 Oct); absent from an older server. */
  rhythm: Array<{ page: string; weeks: number; byWeek?: number[]; tickets: number; rhythm: 'chronic' | 'spike'; examples: Array<{ id: string; title: string }> }>;
  /** How many there are in each list, of which the lists above show some (Codex review round 13). */
  counts: { sameElement: number; chronic: number; areas: number };
  areas: Array<{ area: string; total: number; byType: Record<TicketTypeKey, number>; pageCount: number; pages: Array<{ page: string; tickets: number }> }>;
  reopens: {
    total: number; truncated: boolean; countedFrom: string;
    byPage: Array<{ page: string | null; reopens: number; tickets: Array<{ id: string; title: string }> }>;
    byPerson: Array<{ userId: string; name: string; reopens: number }>;
    /** How many people the reopens are credited to; `byPerson` names the first of them. */
    people: number;
  };
};

export type DashboardTicket = {
  id: string;
  title: string;
  project: { id: string; name: string; color: string };
  column: { id: string; name: string } | null;
  since: string;
  /** What a ticket's block shows (TOADO-860). */
  thumbnailUrl: string | null;
  commentCount: number;
  type: TicketTypeRef | null;
  loggedBy: { name: string; visitor: boolean };
  ai: { comments: boolean; fixes: number };
};

/** The workspace at a glance (16 Oct): its cost a month at list price, its people and seats. */
export type WorkspaceSummary = {
  plan: string;
  interval: 'monthly' | 'annual' | null;
  status: string;
  renewsAt: string | null;
  endsAtPeriodEnd: boolean;
  /** `plan` is null when the plan has no list price; `total` null when its whole cost is not known here (Enterprise). */
  monthlyCents: { plan: number | null; addons: number; widget: number; total: number | null } | null;
  people: { owners: number; members: number; viewers: number; total: number };
  seats: { cap: number; used: number; openInvites: number } | null;
  projects: number;
  activeLast30Days: number;
};

/** Connected systems (TOADO-726, as reworked on 16 Oct). */
export type DashboardSystems = {
  extension: { installed: string | null; latest: string | null; outdated: boolean };
  jira: { connected: boolean; site: string | null; needsReconnect: boolean };
  widgets: Array<{ projectId: string; projectName: string; lastSeenAt: string | null; quiet: boolean }>;
  /** Agents that acted here over the last `agentsSeenDays` days, on tickets I can see. */
  agents: Array<{ agent: string; lastUsedAt: string | null }>;
  agentsSeenDays: number;
  /** My own working agent connections. */
  mine: { agents: number };
  ai: { enabled: boolean; transcription: boolean; models: Array<{ label: string; provider: string; model: string }> };
};

export type ExtensionVersionState = {
  version: string | null;
  status: 'current' | 'outdated' | 'unsupported' | null;
  managedHint: boolean;
};

export type AppFeatures = {
  /** Some workspace of the user's grants walkthrough recording: it may be started. */
  v2Recorder: boolean;
  /**
   * The recorder answers this user: a walkthrough already started may finish,
   * be reviewed and committed, whether or not a new one could start (TOADO-493).
   */
  v2RecorderRoutes: boolean;
};

export type RecordingServerSession = {
  id: string;
  companyId: string;
  projectId: string;
  status: string;
  expiresAt: string;
  /** Whether its clips can be cut again, as the server decides it (TOADO-900). */
  clipsRetryable?: boolean;
  masterUploadStatus?: 'pending' | 'uploading' | 'ready' | 'failed';
  masterUploadOffset?: number;
  masterUploadTotalBytes?: number;
  transcriptionAvailable?: boolean;
  /**
   * Last server-side change, and whether a worker is holding this right now.
   *
   * Both are what let the review page tell a walkthrough that has stopped
   * from one that is simply taking a while (TOADO-304). A heartbeat renews
   * the worker's lease without touching the session row, so `updatedAt` alone
   * cannot answer it; `workerActive` is the lease itself, and is present only
   * while the session is processing.
   */
  updatedAt?: string;
  workerActive?: boolean;
  segments?: RecordingServerSegment[];
};

/** A finished walkthrough, as the recorder page lists it (TOADO-439). */
export type RecordingHistoryItem = {
  id: string;
  companyId: string;
  projectId: string;
  mediaMode: 'video' | 'audio';
  startedAt: string;
  endedAt?: string;
  committedAt?: string;
  createdAt: string;
  /** The moments that became items. */
  itemCount: number;
  /** Tickets it filed that still exist. */
  ticketCount: number;
  /** The first of those tickets, when it is still in the walkthrough's project. */
  firstTicketTitle?: string;
  /** That ticket's picture, for the row (16 Oct). */
  pictureUrl?: string;
  clipsKept: number;
  clipsExpired: number;
  /** When the first clip still stored ages out, under the workspace's plan. */
  videoKeptUntil?: string;
};

/** One numbered page of past walkthroughs, and how many there are altogether. */
export type RecordingHistoryPage = {
  items: RecordingHistoryItem[];
  total: number;
  page: number;
  pageSize: number;
};

export type RecordingServerSegment = {
  id: string;
  sessionId: string;
  startMs: number;
  endMs?: number;
  segmentType: 'bug' | 'feature_request';
  /**
   * Whether a person picked `segmentType`. When false it is only the recorder's
   * default, the ticket is filed untyped, and the classifier chooses (TOADO-416).
   */
  segmentTypeChosen: boolean;
  droppedAt?: string;
  /**
   * `expired` is a clip pruned once its plan's retention window passed
   * (TOADO-367); it never returns. `expiring` is one being removed right now,
   * and is already unavailable, so readers treat the two alike.
   */
  clipUploadStatus?: 'pending' | 'uploading' | 'ready' | 'failed' | 'expired' | 'expiring';
  clipBytes?: number;
  transcriptText?: string;
  transcriptStatus?: 'not_requested' | 'pending' | 'ready' | 'failed';
  transcriptionRequestedAt?: string;
  transcriptAttemptCount?: number;
  transcriptGeneration?: number;
  transcriptLastErrorCode?: string;
  transcriptUsageSeconds?: number;
  transcribedAt?: string;
  draftTitle?: string;
  draftDescription?: string;
  draftDescriptionEdited?: boolean;
  primarySnapshotId?: string;
  ticketId?: string;
  clipUrl?: string;
  snapshots: Array<{
    id: string;
    segmentId: string;
    requestId?: string;
    capturedAtMs: number;
    trigger: 'start' | 'finish' | 'manual';
    orderIndex: number;
    screenshotUrl?: string;
    thumbnailUrl?: string;
    sourceUrl?: string;
    uploadStatus?: 'pending' | 'ready' | 'failed';
    attemptCount?: number;
    lastErrorCode?: string;
    droppedAt?: string;
    captureId?: string;
  }>;
};

// ---------------------------------------------------------------------------
// Local-only state: project view prefs (the live bus is in liveBus.ts)
// ---------------------------------------------------------------------------

const prefsStore: Record<string, ProjectViewPrefs> = {};
const defaultPrefs = (): ProjectViewPrefs => ({
  viewMode: 'board',
  thumbnails: true,
  // Default to user-sorted (drag-and-drop order on the kanban). Users
  // opt into newest/oldest/last-activity/most-comments when they want
  // explicit ordering — picking one of those overrides drag order in
  // every column. See ProjectViewPrefs JSDoc in @vd/types.
  sort: 'user-sorted',
  sidebarCollapsed: false,
  quickFilters: {
    hasAiActivity: false,
    capturePartial: false,
    unresolved: false,
    mentionsMe: false,
    archived: false,
  },
});

// ---------------------------------------------------------------------------
// Public API — matches the shape of mockApi so swapping imports is a rename
// ---------------------------------------------------------------------------

/** One thing in the bell (TOADO-839): who did what to which ticket, and whether it came since the bell was last opened. */
export type NotificationItem = {
  kind: 'assigned' | 'mentioned' | 'commented'; ticketId: string; title: string; projectId: string;
  at: string; actor: string; unread: boolean;
};

/** What a workspace answer says of its mark (TOADO-906). */
export type WorkspaceMarkAnswer = { color: string; logoUrl: string | null; hasUploadedLogo: boolean };

export const api = {
  // Auth
  // Optional during the rolling API/web rollout: older API instances do not
  // include extensionVersion yet, and the web client must remain usable while
  // a mixed release is serving traffic.
  me: () => get<{
    user: User;
    companies: Company[];
    extensionVersion?: ExtensionVersionState;
    // Optional during an additive API/web rollout. A web build talking to an
    // older API must fail closed instead of briefly exposing the recorder.
    features?: Partial<AppFeatures>;
  }>('/me'),
  signIn: (email: string, password: string) =>
    post<{ user: User }>('/auth/signin', { email, password }),
  signOut: () => post<void>('/auth/signout'),
  signUp: (data: { email: string; password: string; name: string; inviteToken?: string }) =>
    post<{ user: User; joinedCompanyId: string | null }>('/auth/signup', data),
  // Public lookup so AcceptInvitePage can render company name + inviter
  // before the recipient is signed in. Possessing the token is the
  // capability — same as accept-invite — but we never echo the token.
  lookupInvite: (token: string) =>
    get<{
      email: string;
      role: 'owner' | 'member' | 'viewer';
      companyName: string;
      companyColor?: string | null;
      inviterName: string;
      hasAccount: boolean;
    }>(`/invites/lookup/${encodeURIComponent(token)}`),
  forgotPassword: (email: string) => post<void>('/auth/forgot', { email }),
  resetPassword: (token: string, password: string) =>
    post<void>('/auth/reset', { token, password }),
  verifyEmail: (token: string) => post<void>('/auth/verify-email', { token }),
  // `init` carries an AbortSignal, which the theme setting needs: its write
  // is fired and not awaited, and the transport retries a 401 once after half
  // a second. Without a signal that retry can leave a window in which one
  // account's choice is re-sent with the next account's cookies.
  updateMe: (
    data: Partial<{ name: string; email: string; avatarUrl: string | null; theme: UserTheme }>,
    init?: { signal?: AbortSignal },
  ) => patch<{ user: User }>('/me', data, init),
  listSessions: () => get<Array<{ id: string; createdAt: string; lastSeenAt: string; ipAddress?: string; userAgent?: string; isCurrent?: boolean; revokedAt?: string }>>('/me/sessions'),
  revokeSession: (sessionId: string) => request<void>(`/me/sessions/${sessionId}`, { method: 'DELETE' }),
  signOutOtherSessions: () => request<void>('/me/sessions', { method: 'DELETE' }),
  getPreferences: () => get<Record<string, unknown>>('/me/preferences'),
  patchPreferences: (prefs: Record<string, unknown>) =>
    patch<Record<string, unknown>>('/me/preferences', prefs),

  // Onboarding questionnaire — fire-and-forget, pure analytics.
  saveOnboardingProfile: (data: { role?: string; experience?: string; primaryUse?: string; referralSource?: string }) =>
    post<{ ok: true }>('/me/onboarding', data),

  // Boolean flag driving the "Get the extension" prompt in the app shell.
  getExtensionStatus: () => get<{ installed: boolean }>('/me/extension-status'),

  // Staff-gated walkthrough recorder. Session, segment, and resumable master
  // upload calls stay together so the browser controller has one contract.
  createRecording: (projectId: string, mediaMode: 'video' | 'audio' = 'video') =>
    post<RecordingServerSession>('/recordings', { projectId, mediaMode }),
  // Every session this user still has open, plus what the per-user cap makes
  // of them. `activeCount` is the cap's own count and can exceed `items`, which
  // is filtered to projects the caller can still see.
  //
  // `activeOnly` narrows to the statuses that hold a slot. The unfiltered list
  // is capped at the newest 50 rows, so committed history can otherwise push a
  // stuck session out of the list that exists to surface it.
  listRecordings: (options?: { activeOnly?: boolean }, signal?: AbortSignal) => request<{
    items: RecordingServerSession[];
    activeCount: number;
    activeLimit: number;
    /** The server's own clock, so elapsed time is measured against one clock. */
    now?: string;
  }>(options?.activeOnly ? '/recordings?active=1' : '/recordings', { method: 'GET', signal }),
  /** Finished walkthroughs in one workspace, newest first, one numbered page at a time (TOADO-439). */
  listRecordingHistory: (
    options: {
      companyId: string;
      page: number;
      pageSize?: number;
      /** Narrowing filters (TOADO-441): one project, and when they were recorded. */
      projectId?: string;
      from?: string;
      to?: string;
    },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({ companyId: options.companyId, page: String(options.page) });
    if (options.pageSize) query.set('pageSize', String(options.pageSize));
    if (options.projectId) query.set('projectId', options.projectId);
    if (options.from) query.set('from', options.from);
    if (options.to) query.set('to', options.to);
    return request<RecordingHistoryPage>(`/recordings/history?${query.toString()}`, { method: 'GET', signal });
  },
  getRecording: (id: string, signal?: AbortSignal) =>
    request<RecordingServerSession>(`/recordings/${encodeURIComponent(id)}`, { method: 'GET', signal }),
  discardRecording: (id: string) =>
    request<void>(`/recordings/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** The recorder is still recording this walkthrough (TOADO-900): its heartbeat. */
  recordingAlive: (id: string) =>
    request<void>(`/recordings/${encodeURIComponent(id)}/alive`, { method: 'POST' }),
  openRecordingSegment: (
    recordingId: string,
    input: { startMs: number; segmentType: 'bug' | 'feature_request' },
  ) => post<RecordingServerSegment>(
    `/recordings/${encodeURIComponent(recordingId)}/segments`,
    input,
  ),
  closeRecordingSegment: (
    recordingId: string,
    segmentId: string,
    endMs: number,
    signal?: AbortSignal,
  ) =>
    request<RecordingServerSegment>(
      `/recordings/${encodeURIComponent(recordingId)}/segments/${encodeURIComponent(segmentId)}/close`,
      { method: 'POST', body: JSON.stringify({ endMs }), signal },
    ),
  cancelRecordingSegment: (recordingId: string, segmentId: string, signal?: AbortSignal) =>
    request<void>(
      `/recordings/${encodeURIComponent(recordingId)}/segments/${encodeURIComponent(segmentId)}`,
      { method: 'DELETE', signal },
    ),
  preflightRecordingSnapshot: (
    recordingId: string,
    signal?: AbortSignal,
    purpose: 'capture' | 'upload' = 'capture',
  ) =>
    request<{ captureMode: 'full' | 'metadata_only' | 'never' }>(
      `/recordings/${encodeURIComponent(recordingId)}/snapshot-policy?purpose=${purpose}`,
      { method: 'GET', signal },
    ),
  retryRecordingTranscription: (recordingId: string, segmentId: string) =>
    post<RecordingServerSegment>(
      `/recordings/${encodeURIComponent(recordingId)}/segments/${encodeURIComponent(segmentId)}/transcription/retry`,
    ),
  updateRecordingReviewSegment: (
    recordingId: string,
    segmentId: string,
    input: Partial<Pick<RecordingServerSegment, 'draftTitle' | 'draftDescription'> & {
      /** Null is "let Toado decide": it withdraws a choice rather than making one. */
      segmentType: RecordingServerSegment['segmentType'] | null;
      /**
       * Sent true with every type a person picked. Without it the server treats a type
       * as a choice only when it changes the stored one, which is what keeps a page
       * from before this protocol from turning the default into a choice. Typed as
       * `true` because this page only ever says yes; "no choice" is sent as a null type.
       */
      segmentTypeChosen: true;
    }>,
  ) => patch<RecordingServerSegment>(
    `/recordings/${encodeURIComponent(recordingId)}/segments/${encodeURIComponent(segmentId)}/review`,
    input,
  ),
  dropRecordingReviewSegment: (recordingId: string, segmentId: string) => request<void>(
    `/recordings/${encodeURIComponent(recordingId)}/segments/${encodeURIComponent(segmentId)}/review`,
    { method: 'DELETE' },
  ),
  updateRecordingReviewSnapshots: (
    recordingId: string,
    segmentId: string,
    input: {
      orderedSnapshotIds?: string[];
      droppedSnapshotIds?: string[];
      primarySnapshotId?: string | null;
    },
  ) => patch<RecordingServerSegment>(
    `/recordings/${encodeURIComponent(recordingId)}/segments/${encodeURIComponent(segmentId)}/review-snapshots`,
    input,
  ),
  commitRecording: (
    recordingId: string,
    drafts: Array<{
      segmentId: string;
      draftTitle: string;
      draftDescription: string;
      /** Null files the ticket untyped for the classifier (TOADO-416). */
      segmentType: RecordingServerSegment['segmentType'] | null;
      /** True with every type a person picked; see `updateRecordingReviewSegment`. */
      segmentTypeChosen?: true;
    }> = [],
  ) => post<{ status: 'committed'; tickets: Ticket[] }>(
    `/recordings/${encodeURIComponent(recordingId)}/commit`,
    { drafts },
  ),
  uploadRecordingSnapshot: async (input: {
    recordingId: string;
    segmentId: string;
    projectId: string;
    requestId: string;
    capturedAtMs: number;
    trigger: 'start' | 'finish' | 'manual';
    sourceUrl: string;
    screenshot: Blob;
    thumbnail: Blob;
  }, signal?: AbortSignal) => {
    const form = new FormData();
    form.append('projectId', input.projectId);
    form.append('sourceUrl', input.sourceUrl);
    form.append('requestId', input.requestId);
    form.append('capturedAtMs', String(input.capturedAtMs));
    form.append('trigger', input.trigger);
    form.append('browser', JSON.stringify({
      userAgent: navigator.userAgent,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      devicePixelRatio: window.devicePixelRatio,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorScheme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    }));
    if (input.screenshot.size > 0) form.append('screenshot', input.screenshot, 'snapshot.png');
    if (input.thumbnail.size > 0) form.append('thumbnail', input.thumbnail, 'thumbnail.webp');
    return request<RecordingServerSegment['snapshots'][number]>(
      `/recordings/${encodeURIComponent(input.recordingId)}/segments/${encodeURIComponent(input.segmentId)}/snapshots`,
      { method: 'POST', body: form, signal },
    );
  },
  getRecordingMasterUpload: async (recordingId: string, signal?: AbortSignal) => {
    const response = await requestRaw(
      `/recordings/${encodeURIComponent(recordingId)}/master`,
      { method: 'HEAD', signal },
    );
    const offsetHeader = response.headers.get('Upload-Offset');
    const offset = Number(offsetHeader);
    const lengthHeader = response.headers.get('Upload-Length');
    const totalBytes = lengthHeader === null ? null : Number(lengthHeader);
    if (offsetHeader === null || !Number.isSafeInteger(offset) || offset < 0
      || (totalBytes !== null && (!Number.isSafeInteger(totalBytes) || totalBytes <= 0))) {
      throw new Error('The recording server returned invalid upload progress.');
    }
    return {
      offset,
      totalBytes,
      status: response.headers.get('Upload-Status') ?? 'pending',
    };
  },
  uploadRecordingMasterPart: async (
    recordingId: string,
    body: Blob,
    input: {
      start: number;
      total: number;
      recordingPartIndex: number;
      recordingPartStartMs: number;
    },
    signal?: AbortSignal,
  ) => {
    const end = input.start + body.size - 1;
    const response = await requestRaw(
      `/recordings/${encodeURIComponent(recordingId)}/master`,
      {
        method: 'PUT',
        body,
        signal,
        headers: {
          'Content-Type': 'video/webm',
          'Content-Range': `bytes ${input.start}-${end}/${input.total}`,
          'Upload-Recording-Part': String(input.recordingPartIndex),
          'Upload-Recording-Part-Start-Ms': String(input.recordingPartStartMs),
        },
      },
    );
    const offset = Number(response.headers.get('Upload-Offset'));
    if (!Number.isSafeInteger(offset) || offset < 0 || offset > input.total) {
      throw new Error('The recording server returned an invalid upload offset.');
    }
    return { offset };
  },
  retryRecordingClips: (recordingId: string, signal?: AbortSignal) => request<{ status: 'uploading' | 'ready' }>(
    `/recordings/${encodeURIComponent(recordingId)}/clips/retry`,
    { method: 'POST', signal },
  ),

  // Multipart avatar upload — sends the file as `file` field. Server
  // validates type/size, stores under avatars/<userId>/, and returns
  // a fetchable URL with cache-bust appended.
  uploadAvatar: async (file: File): Promise<{ ok: true; avatarUrl: string }> => {
    const form = new FormData();
    form.append('file', file);
    return request<{ ok: true; avatarUrl: string }>('/me/avatar', {
      method: 'POST',
      body: form,
    });
  },
  // Clears the avatar reference; the user row's avatar_url goes back to
  // null and the UI falls back to the gradient + initials default.
  deleteAvatar: () => request<{ ok: true }>('/me/avatar', { method: 'DELETE' }),

  // Single-roundtrip data for the HomePage routing decision + welcome
  // checklist. Booleans drive whether each step gets a ✓ checkmark.
  /** `companyId`: count the project and ticket in that workspace alone (17 Oct); account-wide without it. */
  getHomeState: (companyId?: string | null) => get<{
    hasTickets: boolean;
    lastProjectId: string | null;
    hasProject: boolean;
    extensionInstalled: boolean;
    widgetInstalled: boolean;
    mcpConfigured: boolean;
    /** The Dashboard's Getting started bar as this person left it (TOADO-722); null when they never chose. */
    welcomeClosed?: boolean | null;
    /** The release whose What's new card this person dismissed (TOADO-731). */
    whatsNewDismissed?: string | null;
    /** How far this person has got through each tour (TOADO-971); a tour not listed was never started. */
    tours?: Record<string, TourProgress>;
  }>(companyId ? `/me/home-state?companyId=${encodeURIComponent(companyId)}` : '/me/home-state'),
  /** Stores one tour's progress for this person, in every browser. */
  saveTourProgress: (tourId: string, progress: Omit<TourProgress, 'updatedAt'> & { writer?: TourWriter }, signal?: AbortSignal) =>
    request<{ tourId: string; stored: true; progress: TourProgress } | { tourId: string; stored: false }>(`/me/onboarding/tours/${encodeURIComponent(tourId)}`, { method: 'PUT', body: JSON.stringify(progress), signal }),
  /** Opens or closes the Dashboard's Getting started bar, for this person in every browser. */
  setWelcomeClosed: (closed: boolean) => patch<{ welcomeClosed: boolean }>('/me/welcome', { closed }),
  /** Dismisses the What's new card for this release, for this person in every browser. */
  dismissWhatsNew: (release: string) => patch<{ whatsNewDismissed: string }>('/me/whats-new', { release }),

  /** Reports (TOADO-721), Team and up; `query` from reportQuery in features/reports/reportFrame.ts. */
  reports: {
    overview: (query: string) => get<ReportOverview>(`/reports/overview?${query}`),
    errors: (query: string) => get<ReportErrors>(`/reports/errors?${query}`),
    pages: (query: string) => get<ReportPages>(`/reports/pages?${query}`),
    trend: (query: string) => get<ReportTrend>(`/reports/trend?${query}`),
    timeToFix: (query: string) => get<ReportTimeToFix>(`/reports/time-to-fix?${query}`),
    team: (query: string) => get<ReportTeam>(`/reports/team?${query}`),
    people: (query: string) => get<ReportPeople>(`/reports/people?${query}`),
    patterns: (query: string) => get<ReportPatterns>(`/reports/patterns?${query}`),
    collection: (query: string) => get<ReportCollection>(`/reports/collection?${query}`),
  },

  /** The Dashboard's sections (TOADO-720), each for one workspace; shapes in apps/api/src/dashboard.ts. */
  dashboard: {
    needsYou: (companyId: string) => get<{ assigned: DashboardTicket[]; waitingOnYou: DashboardTicket[] }>(`/me/dashboard/needs-you?companyId=${encodeURIComponent(companyId)}`),
    goneQuiet: (companyId: string) => get<{ tickets: DashboardTicket[]; quietDays: number }>(`/me/dashboard/gone-quiet?companyId=${encodeURIComponent(companyId)}`),
    review: (companyId: string) => get<{
      walkthroughs: Array<{ id: string; projectId: string; projectName: string; projectColor: string; endedAt: string | null; items: number; thumbnailUrl: string | null }>;
      intake: Array<{ projectId: string; projectName: string; waiting: number }>;
      intakeTickets: DashboardTicket[];
    }>(`/me/dashboard/review?companyId=${encodeURIComponent(companyId)}`),
    systems: (companyId: string) => get<DashboardSystems>(`/me/dashboard/systems?companyId=${encodeURIComponent(companyId)}`),
    agentActivity: (companyId: string) => get<{ since: string; readAt: string; hasAgent: boolean; lines: Array<{ agent: string; did: string; ticket: DashboardTicket; at: string }> }>(`/me/dashboard/agent-activity?companyId=${encodeURIComponent(companyId)}`),
    markSeen: (companyId: string, upTo: string) => post<void>('/me/dashboard/seen', { companyId, upTo }),
    recentTickets: (companyId: string) => get<{ tickets: Array<DashboardTicket & { openedAt: string }> }>(`/me/dashboard/recent-tickets?companyId=${encodeURIComponent(companyId)}`),
    /** Your numbers' trends (16 Oct): the last seven days against the seven before. */
    trends: (companyId: string) => get<Record<'assigned' | 'created' | 'mentioned', { thisWeek: number; lastWeek: number; daily?: number[] }>>(`/me/dashboard/trends?companyId=${encodeURIComponent(companyId)}`),
    team: (companyId: string) => get<{ members: Array<{ userId: string; name: string; role: string; ticketsThisMonth: number; lastActiveAt: string | null }> }>(`/me/dashboard/team?companyId=${encodeURIComponent(companyId)}`),
    /** The workspace at a glance (16 Oct), owners only. */
    workspaceSummary: (companyId: string) => get<WorkspaceSummary>(`/me/dashboard/workspace-summary?companyId=${encodeURIComponent(companyId)}`),
    planUsage: (companyId: string) => get<{
      plan: string;
      tickets: { used: number | null; limit: number | null };
      recordingMinutes: { used: number; limit: number | null };
      narrationMinutes: { used: number; limit: number | null };
      storageBytes: { used: number; limit: number | null };
    }>(`/me/dashboard/plan-usage?companyId=${encodeURIComponent(companyId)}`),
  },
  /** The person opened this ticket (TOADO-730), for Pick up where you left off. */
  ticketOpened: (ticketId: string) => post<void>(`/tickets/${ticketId}/opened`, {}),

  // Create the user's first company (post-signup onboarding wizard).
  createCompany: (data: { name: string }) => post<{ id: string; name: string; role: 'owner' }>('/companies', data),

  // Companies / projects / columns
  getCompany: (companyId: string) => get<{ id: string; name: string; aiEnabled: boolean } & WorkspaceMarkAnswer>(`/companies/${companyId}`),
  updateCompany: (companyId: string, data: Partial<{ name: string; color: string; aiEnabled: boolean }>) =>
    patch<{ id: string; name: string; aiEnabled: boolean } & WorkspaceMarkAnswer>(`/companies/${companyId}`, data),
  /** A workspace's logo (TOADO-906): PNG or JPEG, drawn again by the server as a 64px PNG. */
  uploadWorkspaceLogo: (companyId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<WorkspaceMarkAnswer>(`/companies/${companyId}/logo`, { method: 'POST', body: form });
  },
  removeWorkspaceLogo: (companyId: string) => request<WorkspaceMarkAnswer>(`/companies/${companyId}/logo`, { method: 'DELETE' }),
  deleteCompany: (companyId: string) => request<void>(`/companies/${companyId}`, { method: 'DELETE' }),
  getJiraConnection: (companyId: string) => get<{ status: 'not_connected' | 'connected' | 'disconnected'; siteUrl?: string; connectedAt?: string; disconnectReason?: string;
    /** The Toado person who connected it; null if they have left. */
    connectedByName?: string | null;
    /** Whether Toado hears from Jira that a sent issue was deleted (TOADO-712); null while that is off. */
    deletedIssues?: { status: 'watching' | 'needs_reconnect' | 'no_public_url' | 'error' | null; error: string | null } | null;
    /** Whether the workspace's plan has Jira export: Team and up (TOADO-713). */
    planHasJira?: boolean }>(`/companies/${companyId}/jira`),
  connectJira: (companyId: string) => post<{ authorizationUrl: string }>(`/companies/${companyId}/jira/connect`, {}),
  disconnectJira: (companyId: string) => request<void>(`/companies/${companyId}/jira`, { method: 'DELETE' }),
  /** The sites a multi-site Atlassian sign-in offers this session (TOADO-262); 404 once used or expired. */
  getJiraPending: (companyId: string) => get<{ round: string; expiresAt: string; secondsLeft: number; current: { cloudId: string; siteUrl: string } | null;
    sites: Array<{ cloudId: string; url: string; name: string; remapCount: number }> }>(`/companies/${companyId}/jira/pending`),
  /** `round` is the one the picker showed: a choice from a sign-in a newer one replaced is refused (404). */
  selectJiraSite: (companyId: string, cloudId: string, round: string) =>
    post<{ status: 'connected'; siteUrl: string }>(`/companies/${companyId}/jira/select`, { cloudId, round }),
  cancelJiraPending: (companyId: string, round: string) =>
    request<void>(`/companies/${companyId}/jira/pending?round=${encodeURIComponent(round)}`, { method: 'DELETE' }),
  getJiraMetadata: (companyId: string) => get<{ cloudId: string; projects: Array<{ id: string; key: string; name: string; issueTypes?: Array<{ id: string; name: string; subtask?: boolean }> }> }>(`/companies/${companyId}/jira/metadata`),
  getJiraMapping: (projectId: string) => get<{ status: 'not_mapped' | 'mapped' | 'stale'; mapping?: { jiraProjectId: string; jiraProjectKey: string; jiraProjectName: string; issueTypeId: string; issueTypeName: string; issueTypesByTicketType?: Record<string, { id: string; name: string }>; defaultLabels: string[] } }>(`/projects/${projectId}/jira-mapping`),
  saveJiraMapping: (projectId: string, data: { jiraProjectId: string; issueTypeId: string; issueTypesByTicketType: Record<string, string>; defaultLabels: string[] }) =>
    request<{ status: 'mapped'; mapping: unknown }>(`/projects/${projectId}/jira-mapping`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteJiraMapping: (projectId: string) => request<void>(`/projects/${projectId}/jira-mapping`, { method: 'DELETE' }),
  // Jira auto-send (Jason, 18 Oct): null when it is off.
  getJiraAutomation: (projectId: string) => get<{ automation: JiraAutomation | null }>(`/projects/${projectId}/jira-automation`),
  saveJiraAutomation: (projectId: string, data: { enabled: false } | ({ enabled: true } & Omit<JiraAutomation, 'enabledAt'>)) =>
    request<{ automation: JiraAutomation | null }>(`/projects/${projectId}/jira-automation`, { method: 'PUT', body: JSON.stringify(data) }),
  getJiraExport: (ticketId: string) => get<{
    available: boolean; state: 'not_exported' | 'pending' | 'exported' | 'failed'; issueKey?: string; issueUrl?: string; attachmentState: string; error?: string;
    /** The walkthrough video on the issue (TOADO-704); null for a ticket with none, or not sent. */
    video: { state: 'attached' | 'pending' | 'failed' | 'not_attached'; message: string | null } | null;
    /** Jira says it has no such issue (TOADO-712): deleted, or out of the connected account's sight. */
    issueMissing?: boolean;
    /** What Jira has as the issue's type, beside what the ticket's type maps to (TOADO-821); null when Toado never learned it. */
    jiraType?: { inJira: string; wanted: string | null; differs: boolean; note: string | null } | null;
  }>(`/tickets/${ticketId}/jira-export`),
  /** Asks for a sent ticket's walkthrough video to be attached to its issue again (TOADO-704). */
  attachJiraVideo: (ticketId: string) => post<{ state: 'pending' }>(`/tickets/${ticketId}/jira-video`, {}),
  exportTicketToJira: (ticketId: string) => post<{ state: 'pending' | 'exported'; issueKey?: string; issueUrl?: string }>(`/tickets/${ticketId}/jira-export`, {}),
  /** Forgets a ticket's Jira issue, which stays in Jira, so it can be sent afresh. */
  unlinkTicketFromJira: (ticketId: string) => request<{ state: 'not_exported' }>(`/tickets/${ticketId}/jira-export`, { method: 'DELETE' }),
  /** Whether this project's tickets can be sent to Jira, and whether this person may send them (TOADO-708). */
  getProjectJiraSend: (projectId: string) => get<{ available: boolean; canSend: boolean;
    /** Unlinking asks nothing of Jira: offered to members whatever the plan or connection (TOADO-713). */
    canUnlink?: boolean;
    /** The workspace's plan is below Team, so nothing new goes to Jira (TOADO-713). */
    requiresTeam?: boolean }>(`/projects/${projectId}/jira-export`),
  /** Sends several tickets at once; the counts say what happened to each (TOADO-708). */
  sendTicketsToJira: (projectId: string, ticketIds: string[]) =>
    post<{ queued: number; alreadyInJira: number; alreadySending: number; failed: number; notFound: number }>(`/projects/${projectId}/jira-export`, { ticketIds }),

  // Capture profiles
  listCaptureProfiles: (companyId: string) =>
    get<Array<{ id: string; companyId: string; name: string; originPattern: string; mode: 'full' | 'metadata_only' | 'never'; extraRedactions: { headers?: string[]; urlParams?: string[] } }>>(`/companies/${companyId}/capture-profiles`),
  createCaptureProfile: (companyId: string, data: { name: string; originPattern: string; mode?: 'full' | 'metadata_only' | 'never'; extraRedactions?: { headers?: string[]; urlParams?: string[] } }) =>
    post<{ id: string; name: string; originPattern: string; mode: 'full' | 'metadata_only' | 'never'; extraRedactions: { headers?: string[]; urlParams?: string[] } }>(`/companies/${companyId}/capture-profiles`, data),
  updateCaptureProfile: (companyId: string, id: string, data: Partial<{ name: string; originPattern: string; mode: 'full' | 'metadata_only' | 'never'; extraRedactions: { headers?: string[]; urlParams?: string[] } }>) =>
    patch<{ id: string }>(`/companies/${companyId}/capture-profiles/${id}`, data),
  deleteCaptureProfile: (companyId: string, id: string) =>
    request<void>(`/companies/${companyId}/capture-profiles/${id}`, { method: 'DELETE' }),

  listProjects: (companyId: string) => get<Project[]>(`/companies/${companyId}/projects`),
  getProject: (projectId: string) => get<Project>(`/projects/${projectId}`),
  listColumns: (projectId: string) => get<Column[]>(`/projects/${projectId}/columns`).then(withColumnTones),
  createProject: (
    companyId: string,
    data: { name: string; color?: string; description?: string; urlPattern?: string; urlPatterns?: string[] },
  ) => post<Project>(`/companies/${companyId}/projects`, data),
  /** The workspace tour's sample ticket on this board: made, or the one already there (TOADO-988). */
  startTourSample: (projectId: string) => post<{ id: string }>(`/projects/${projectId}/sample-ticket`, {}),
  /** And taken away when the tour ends. */
  endTourSample: (projectId: string) => request<{ removed: number }>(`/projects/${projectId}/sample-ticket`, { method: 'DELETE' }),
  updateProject: (
    projectId: string,
    data: Partial<{ name: string; description: string | null; color: string; urlPatterns: string[]; archivalDays: number | null; bugIntakeEnabled: boolean; recordingMediaMode: 'video' | 'audio' | null; repositoryUrl: string | null; iconChoice: 'auto' | 'initials'; iconSite: string | null; agentInstructions: string | null }>,
  ) => patch<Project>(`/projects/${projectId}`, data),
  /** A project's own icon over its site's (16 Oct): PNG or JPEG, drawn again by the server as a 64px PNG. */
  uploadProjectIcon: (projectId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<ProjectIconAnswer>(`/projects/${projectId}/icon`, { method: 'POST', body: form });
  },
  removeProjectIcon: (projectId: string) => request<ProjectIconAnswer>(`/projects/${projectId}/icon`, { method: 'DELETE' }),
  /** Look for the site's icon again now; the answer comes with the next read of the project. */
  refreshProjectIcon: (projectId: string) => post<{ queued: true }>(`/projects/${projectId}/icon/refresh`, {}),
  deleteProject: (projectId: string) => request<void>(`/projects/${projectId}`, { method: 'DELETE' }),

  // Embedded widget (owner-facing; the public surface is called by the SDK,
  // not from here). `getWidget` is a POST because the widget row is created
  // lazily on first access — a GET that writes would be a lie.
  getWidget: (projectId: string) => post<ProjectWidget>(`/projects/${projectId}/widget`, {}),
  /** Owner-only cross-project widget overview (Company Settings -> Bug report widget). */
  listCompanyWidgets: (companyId: string) =>
    get<WidgetOverviewItem[]>(`/companies/${companyId}/widgets`),
  // The PATCH response carries an extra `addedUrlPatterns`: when a save
  // grows the origin allowlist, the API mirrors the new origins into the
  // project's URL patterns and reports which it added (TOADO-135) so the
  // Studio can surface it. Empty on every save that did not add an origin.
  updateWidget: (projectId: string, data: UpdateProjectWidgetBody) =>
    patch<ProjectWidget & { addedUrlPatterns?: string[] }>(`/projects/${projectId}/widget`, data),
  rotateWidgetKey: (projectId: string) =>
    post<ProjectWidget>(`/projects/${projectId}/widget/rotate-key`, {}),
  /**
   * "Try it live" (TOADO-894): keep the settings being edited for a short
   * while under a token the test page loads the real widget with. Owners only.
   */
  startWidgetTrial: (projectId: string, draft: Pick<ProjectWidget, 'captureTier' | 'launcher' | 'dialog' | 'theme' | 'fields' | 'privacy'>) =>
    post<{ token: string; key: string; expiresAt: string; recordingNote: string }>(`/projects/${projectId}/widget/trial`, draft),
  /**
   * Upload a custom launcher icon (PNG or SVG). Multipart, sent as the
   * `file` field. The server validates + stores it, points the launcher at
   * the uploaded icon (`launcher.icon.kind === 'upload'`), and returns the
   * updated widget. The bytes are served from the public
   * `/public/widgets/:key/icon` route the SDK builds itself.
   */
  uploadWidgetIcon: async (projectId: string, file: File): Promise<ProjectWidget> => {
    const form = new FormData();
    form.append('file', file);
    return request<ProjectWidget>(`/projects/${projectId}/widget/icon`, {
      method: 'POST',
      body: form,
    });
  },
  /** Remove the custom icon; the launcher falls back to the default glyph. */
  deleteWidgetIcon: (projectId: string) =>
    request<ProjectWidget>(`/projects/${projectId}/widget/icon`, { method: 'DELETE' }),
  /**
   * Claim a widget handoff token (TOADO-136): the /x/handoff page calls this
   * with the user's session to turn a parked capture into a draft ticket
   * they own. Single-use; errors carry status (403 no permission, 404
   * gone/no-access, 410 expired, 402 over cap). The token rides in the body,
   * not the path, so this capability stays out of access logs.
   */
  claimHandoff: (token: string) =>
    post<{ ticketId: string }>('/widgets/handoff/claim', { token }),
  /**
   * Generate a theme proposal from the owner's own site. Stores the
   * proposal on the widget; applying it is a separate, explicit PATCH.
   */
  generateWidgetStyle: (projectId: string, url: string) =>
    post<ProjectWidget>(`/projects/${projectId}/widget/auto-style`, { url }),
  /**
   * Cheap read for the install poll. Separate from `getWidget` because
   * that one is a POST that lazily creates rows — an unreasonable thing to
   * hit every few seconds.
   */
  getWidgetVerification: (projectId: string) =>
    get<WidgetVerification>(`/projects/${projectId}/widget/verification`),
  /** This month's visitor recording for the owner (TOADO-545): used, held, turned away. */
  getWidgetRecordingUsage: (projectId: string) =>
    get<{ month: string; usedSeconds: number; reservedSeconds: number; refusedCreates: number; capSeconds: number }>(
      `/projects/${projectId}/widget/recording-usage`,
    ),
  touchProject: (projectId: string) => post<{ ok: true }>(`/projects/${projectId}/touch`, {}),
  replaceColumns: (projectId: string, columns: Array<Partial<Column> & { name: string }>) =>
    post<Column[]>(`/projects/${projectId}/columns`, { columns }).then(withColumnTones),
  /**
   * Gives the board a column that waits on the reporter (TOADO-970): adds a
   * Response Required column, or marks one it has. A board with one already
   * answers with it, so asking twice adds nothing.
   */
  setWaitingStage: (projectId: string, body: { add: true } | { columnId: string }) =>
    post<{ waitingColumnId: string; created: boolean; columns: Column[] }>(`/projects/${projectId}/columns/waiting-stage`, body)
      .then((answer) => ({ ...answer, columns: withColumnTones(answer.columns) })),

  // Tickets
  /**
   * `typeIds` filters server-side (TOADO-411), including the literal `'none'`
   * for untyped tickets. Filtered there rather than in the browser so it composes
   * with `status`, which is already server-side: one dimension filtered in SQL and
   * another in the client produces a result neither describes.
   */
  listTickets: (
    projectId: string,
    status?: 'active' | 'archived' | 'drafts' | 'all',
    typeIds?: readonly string[],
  ) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (typeIds?.length) params.set('type', typeIds.join(','));
    const query = params.toString();
    return get<{ items: TicketSummary[] }>(`/projects/${projectId}/tickets${query ? `?${query}` : ''}`);
  },
  /** The board's summaries of just these tickets, as its list would give them (t-UI8InD). */
  listTicketSummaries: (projectId: string, ticketIds: readonly string[]) =>
    get<{ items: TicketSummary[] }>(`/projects/${projectId}/tickets?ids=${ticketIds.map(encodeURIComponent).join(',')}`),
  // The sidebar's numbers for one workspace (TOADO-740).
  getInboxCounts: (companyId: string) =>
    get<{ assigned: number; created: number; mentioned: number; team: number }>(
      `/me/inbox/counts?companyId=${encodeURIComponent(companyId)}`,
    ),
  // Opening Mentioned or Team activity clears its number.
  // The bell's list for one workspace, newest first (TOADO-839).
  listNotifications: (companyId: string) =>
    get<{ items: NotificationItem[]; unread: number; readAt: string }>(`/me/notifications?companyId=${encodeURIComponent(companyId)}`),
  /** `upTo` is a read time the server gave: seen only up to what was shown. */
  markInboxSeen: (companyId: string, list: 'mentioned' | 'team' | 'notifications', upTo?: string) =>
    post<void>('/me/inbox/seen', upTo ? { companyId, list, upTo } : { companyId, list }),
  // The current workspace's only: a ticket from another never shows here (TOADO-830).
  // `readAt` is when the server read it, for marking the list seen up to what it showed.
  listInbox: async (kind: 'assigned' | 'created' | 'mentioned' | 'team', companyId: string) => {
    const response = await requestRaw(`/me/inbox?kind=${kind}&companyId=${encodeURIComponent(companyId)}`);
    return { items: await response.json() as TicketSummary[], readAt: response.headers.get('x-toado-read-at') };
  },
  getTicket: (ticketId: string) => get<Ticket>(`/tickets/${ticketId}`),
  /**
   * What the timeline says about the visit, for the Session window: its
   * earlier reports from the same tab, as tickets this person can open
   * (TOADO-948). One entry is asked for; only the context is wanted.
   */
  getTicketVisit: (ticketId: string) =>
    get<{ context?: { earlierReports?: EarlierReports } }>(`/tickets/${encodeURIComponent(ticketId)}/timeline?limit=1`),
  createTextTicket: (projectId: string, title: string, columnId?: string) =>
    post<Ticket>(`/projects/${projectId}/tickets`, columnId ? { title, columnId } : { title }),
  updateTicket: (
    ticketId: string,
    fields: Partial<{
      title: string; description: string; resolved: boolean; branchName: string | null; isDraft: boolean;
      /** Null clears it. See TOADO-409 for why an untyped ticket is a legal state. */
      typeId: string | null;
    }>,
  ) => patch<Ticket>(`/tickets/${ticketId}`, fields),
  /**
   * `beforeTicketId` / `afterTicketId` land it next to that ticket; `index` is
   * a rank among every ticket in the column, hidden ones included.
   */
  moveTicket: (ticketId: string, data: {
    columnId?: string; projectId?: string; index?: number; beforeTicketId?: string; afterTicketId?: string;
  }) =>
    post<Ticket>(`/tickets/${ticketId}/move`, data),
  /**
   * Up to BULK_MOVE_MAX_TICKETS tickets to the end of one column, one at a
   * time in the order given, with a result per ticket (TOADO-976).
   */
  bulkMoveTickets: (ticketIds: string[], target: { columnId?: string; projectId?: string }) =>
    post<{ results: BulkMoveResult[] }>('/tickets/bulk-move', { ticketIds, ...target }),
  assignTicket: (ticketId: string, userIds: string[], mode: 'add' | 'replace') =>
    post<Ticket>(`/tickets/${ticketId}/assign`, { userIds, mode }),
  /**
   * Remove the given users from a ticket's assignee list. Idempotent —
   * users not currently assigned are silently ignored on the server.
   */
  unassignTicket: (ticketId: string, userIds: string[]) =>
    post<Ticket>(`/tickets/${ticketId}/unassign`, { userIds }),
  /**
   * Who a walkthrough's narration named for this ticket (TOADO-474): the people it
   * offers, names it could not place, and matches a guard stopped. It assigns
   * only when its deterministic checks also accept an explicit unique request.
   */
  listNarrationAssignments: (ticketId: string) =>
    get<NarrationAssignmentsResponse>(`/tickets/${ticketId}/narration-assignments`),
  /** Assign somebody a note offers: the person it named, a candidate for an ambiguous name, or one a guard stopped. */
  assignFromNarration: (ticketId: string, noteId: string, userId: string) =>
    post<{ assignees: string[] }>(`/tickets/${ticketId}/narration-assignments/${noteId}/assign`, { userId }),
  dismissNarrationAssignment: (ticketId: string, noteId: string) =>
    post<{ dismissed: true }>(`/tickets/${ticketId}/narration-assignments/${noteId}/dismiss`),
  archiveTicket: (ticketId: string) => post<Ticket>(`/tickets/${ticketId}/archive`),
  restoreTicket: (ticketId: string) => post<Ticket>(`/tickets/${ticketId}/restore`),
  deleteTicket: (ticketId: string) => request<void>(`/tickets/${ticketId}`, { method: 'DELETE' }),
  duplicateTicket: (ticketId: string) => post<Ticket>(`/tickets/${ticketId}/duplicate`),
  submitCapture: (payload: {
    projectId: string;
    sourceUrl?: string;
    title?: string;
    capturedAt?: string;
    screenshot?: string; // data URL
    console?: unknown[];
    network?: unknown[];
    errors?: unknown[];
    performance?: unknown;
    health?: { status: 'complete' | 'partial' | 'failed'; message?: string; captured?: Record<string, boolean> };
  }) => post<Ticket>('/captures', payload),

  // Annotation raster — composite of image + drawn shapes, saved so cards
  // and MCP see the annotated view. Client computes the PNG via canvas
  // compositing and POSTs the data URL.
  saveAnnotationRaster: (
    ticketId: string,
    dataUrl: string,
    opts: { annotationVersion: number; signal?: AbortSignal },
  ) => request<{ ok: true; key: string }>(`/tickets/${ticketId}/annotation-raster`, {
    method: 'POST',
    body: JSON.stringify({ dataUrl, annotationVersion: opts.annotationVersion }),
    signal: opts.signal,
  }),

  // Annotation shapes — the editable source of truth for canvas
  // drawings. Different from the raster above: this saves the JSON
  // shape stack so other team members can see and edit the same
  // shapes when they open the ticket. Without this, annotations
  // exist only in the original author's localStorage and the rest
  // of the team sees only the flattened raster pixels.
  /**
   * TOADO-261: persist an extra carousel image server-side. `imageId`
   * is the carousel id shapes are tagged by - passing the client's own
   * id keeps existing drawings attached (the migration path) and makes
   * the call idempotent per (ticket, imageId).
   */
  uploadTicketImage: (ticketId: string, body: { dataUrl: string; label?: string; imageId?: string }) =>
    post<{ id: string; imageId: string; label: string; url: string; sizeBytes: number; createdAt: string }>(
      `/tickets/${ticketId}/attachments`, body,
    ),
  deleteTicketImage: (ticketId: string, attachmentId: string) =>
    request<void>(`/tickets/${ticketId}/attachments/${attachmentId}`, { method: 'DELETE' }),
  saveAnnotations: (
    ticketId: string,
    snapshot: unknown,
    opts: {
      targetKind?: 'ticket' | 'attachment';
      targetId?: string;
      expectedVersion?: number;
      signal?: AbortSignal;
    } = {},
  ) => request<{ ok: true; snapshot?: unknown; version?: number }>(`/tickets/${ticketId}/annotations`, {
    method: 'POST',
    body: JSON.stringify({
      targetKind: opts.targetKind ?? 'ticket',
      targetId: opts.targetId ?? ticketId,
      snapshot,
      expectedVersion: opts.expectedVersion,
    }),
    signal: opts.signal,
  }),

  /** What fixed a ticket, as recorded (TOADO-844), and where its project's code lives. */
  listTicketFixes: (ticketId: string) => get<{ fixes: TicketFix[]; repositoryUrl: string | null }>(`/tickets/${ticketId}/fixes`),

  // Comments
  listComments: (ticketId: string) => get<Comment[]>(`/tickets/${ticketId}/comments`),
  addComment: (ticketId: string, body: string) =>
    post<Comment>(`/tickets/${ticketId}/comments`, { body }),
  updateComment: (ticketId: string, commentId: string, body: string) =>
    patch<Comment>(`/tickets/${ticketId}/comments/${commentId}`, { body }),
  deleteComment: (ticketId: string, commentId: string) =>
    request<void>(`/tickets/${ticketId}/comments/${commentId}`, { method: 'DELETE' }),

  // Members / invites / tokens
  // 'viewer' added per VIEWER_SEATS_PRD.md — read-only seat that doesn't
  // count against `seatsPurchased` (Stripe billing).
  listMembers: (companyId: string) =>
    get<{
      user: User;
      role: 'owner' | 'member' | 'viewer';
      accessMode: 'all_projects' | 'restricted';
      projectIds: string[];
    }[]>(`/companies/${companyId}/members`),
  listInvites: (companyId: string) => get<Invite[]>(`/companies/${companyId}/invites`),
  inviteMembers: (
    companyId: string,
    invites: Array<{ email: string; role: 'owner' | 'member' | 'viewer' }>,
    options: {
      delivery?: 'email' | 'link' | 'both';
      // Per-batch project access. When omitted, server defaults to
      // 'all_projects' — preserves legacy invite behavior.
      accessMode?: 'all_projects' | 'restricted';
      projectIds?: string[];
    } = {},
  ) =>
    post<Array<Invite & { inviteUrl: string }>>(
      `/companies/${companyId}/invites`,
      { invites, delivery: options.delivery ?? 'email', accessMode: options.accessMode, projectIds: options.projectIds },
    ),
  resendInvite: (inviteId: string) =>
    post<{ inviteUrl: string }>(`/invites/${inviteId}/resend`),
  // Seat math for the invite modal: shows current usage vs. cap so an
  // owner sees "you'll need 2 more seats" before hitting submit.
  getSeatSummary: (companyId: string) =>
    get<{
      plan: 'free' | 'personal' | 'team' | 'business' | 'enterprise';
      cap: number;
      currentMembers: number;
      openInvites: number;
      seatsAvailable: number;
    }>(`/companies/${companyId}/seat-summary`),

  // Two-call seat-quantity adjustment. Preview returns the prorated
  // amount (positive = charged today, negative = credited to next
  // invoice) so the user sees the cost before confirming. Apply
  // requests confirm: true to actually update the Stripe subscription;
  // the webhook then updates companies.seatsPurchased asynchronously.
  previewSeatChange: (companyId: string, quantity: number) =>
    post<{
      ok: boolean;
      unchanged?: boolean;
      currentQuantity: number;
      nextQuantity: number;
      prorationAmount: number;
      currency: string;
      nextBillDate: string | null;
    }>('/billing/seats', { companyId, quantity, confirm: false }),
  applySeatChange: (companyId: string, quantity: number) =>
    post<{ ok: boolean; applied?: boolean; unchanged?: boolean; currentQuantity: number; nextQuantity: number }>(
      '/billing/seats',
      { companyId, quantity, confirm: true },
    ),
  revokeInvite: (inviteId: string) => request<void>(`/invites/${inviteId}`, { method: 'DELETE' }),
  updateMemberRole: (companyId: string, userId: string, role: 'owner' | 'member' | 'viewer') =>
    patch<{ user: User; role: 'owner' | 'member' | 'viewer' }>(`/companies/${companyId}/members/${userId}`, { role }),
  removeMember: (companyId: string, userId: string) =>
    request<void>(`/companies/${companyId}/members/${userId}`, { method: 'DELETE' }),
  updateMemberAccess: (
    companyId: string,
    userId: string,
    body: { accessMode: 'all_projects' | 'restricted'; projectIds?: string[] },
  ) =>
    patch<{ accessMode: 'all_projects' | 'restricted'; projectIds: string[] }>(
      `/companies/${companyId}/members/${userId}/access`,
      body,
    ),
  acceptInvite: (token: string) => post<{ company: Company }>('/auth/accept-invite', { token }),
  /**
   * Ticket types (TOADO-409, TOADO-410, TOADO-411).
   *
   * The PROJECT set is what most surfaces want, because it has the project's
   * renames and hidden types already applied by the server. Resolving a delta on
   * a client is how two clients come to disagree about what a project offers, so
   * nothing here recomputes it.
   */
  listCompanyTicketTypes: (companyId: string) =>
    get<{ items: TicketType[] }>(`/companies/${companyId}/ticket-types`),
  listProjectTicketTypes: (projectId: string) =>
    get<{ items: ProjectTicketType[]; offeredIds: string[] }>(`/projects/${projectId}/ticket-types`),
  createTicketType: (companyId: string, body: { name: string; glyph: string; defaultForNewProjects?: boolean }) =>
    post<TicketType>(`/companies/${companyId}/ticket-types`, body),
  updateTicketType: (
    companyId: string,
    typeId: string,
    body: Partial<{ name: string; glyph: string; order: number; defaultForNewProjects: boolean }>,
  ) => patch<TicketType>(`/companies/${companyId}/ticket-types/${typeId}`, body),
  /**
   * Every type's count for a project's settings list, owner only. `ticketCount` is
   * what a delete would touch (deleted tickets too), `liveTicketCount` the ones not
   * deleted (archived included), `projectTicketCount` what this project's board
   * shows, and `archivedTicketCount` the archived ones across the workspace. A type
   * no ticket uses is absent.
   */
  projectTicketTypeUsage: (projectId: string) =>
    get<{ items: Array<{
      typeId: string; ticketCount: number; liveTicketCount: number; projectTicketCount: number;
      /** Absent from an API before TOADO-599. */
      archivedTicketCount?: number;
    }> }>(
      `/projects/${projectId}/ticket-types/usage`,
    ),
  /** How many tickets a delete would touch, asked BEFORE offering the choice. */
  ticketTypeUsage: (companyId: string, typeId: string) =>
    get<{ ticketCount: number }>(`/companies/${companyId}/ticket-types/${typeId}/usage`),
  /**
   * One of `reassignTo` or `clear` is required when tickets use the type. The
   * server refuses with a 409 naming the count otherwise, rather than letting a
   * destructive choice be made by omission. `clearDeleted` takes it off deleted
   * tickets only, and is refused if any ticket that is not deleted holds it
   * (TOADO-599).
   */
  deleteTicketType: (
    companyId: string,
    typeId: string,
    disposition: { reassignTo?: string; clear?: boolean; clearDeleted?: boolean },
  ) => {
    const query = disposition.reassignTo
      ? `?reassignTo=${encodeURIComponent(disposition.reassignTo)}`
      : disposition.clear ? '?clear=1' : disposition.clearDeleted ? '?clearDeleted=1' : '';
    return request<{ ok: true; ticketCount: number; reassignedTo: string | null }>(
      `/companies/${companyId}/ticket-types/${typeId}${query}`,
      { method: 'DELETE' },
    );
  },
  /** A project's delta. Only the fields named are written, so a reorder stays a reorder. */
  updateProjectTicketType: (
    projectId: string,
    typeId: string,
    body: Partial<{ hidden: boolean; name: string | null; order: number }>,
  ) => patch<{ items: ProjectTicketType[] }>(`/projects/${projectId}/ticket-types/${typeId}`, body),
  /** Adding from a project promotes the type into the company set. See TOADO-408. */
  addProjectTicketType: (projectId: string, body: { name: string; glyph: string }) =>
    post<TicketType>(`/projects/${projectId}/ticket-types`, body),


  // Search
  searchTickets: (q: string, scope: {
    companyId?: string;
    projectId?: string;
    limit?: number;
    /** Subset of {title, description, userComments, aiComments, url}; default is all five. */
    matchIn?: ReadonlyArray<'title' | 'description' | 'userComments' | 'aiComments' | 'url'>;
    includeArchived?: boolean;
    annotated?: 'yes' | 'no' | 'both';
    status?: 'resolved' | 'unresolved' | 'all';
    assigneeIds?: ReadonlyArray<string>;
    authorIds?: ReadonlyArray<string>;
    /** ISO date strings (YYYY-MM-DD or full ISO timestamps). */
    createdFrom?: string;
    createdTo?: string;
    updatedFrom?: string;
    updatedTo?: string;
  } = {}) => {
    const p = new URLSearchParams({ q });
    if (scope.companyId) p.set('companyId', scope.companyId);
    if (scope.projectId) p.set('projectId', scope.projectId);
    if (scope.limit) p.set('limit', String(scope.limit));
    if (scope.matchIn && scope.matchIn.length > 0) p.set('matchIn', scope.matchIn.join(','));
    if (scope.includeArchived) p.set('includeArchived', 'true');
    if (scope.annotated && scope.annotated !== 'both') p.set('annotated', scope.annotated);
    if (scope.status && scope.status !== 'all') p.set('status', scope.status);
    if (scope.assigneeIds && scope.assigneeIds.length > 0) p.set('assigneeIds', scope.assigneeIds.join(','));
    if (scope.authorIds && scope.authorIds.length > 0) p.set('authorIds', scope.authorIds.join(','));
    if (scope.createdFrom) p.set('createdFrom', scope.createdFrom);
    if (scope.createdTo) p.set('createdTo', scope.createdTo);
    if (scope.updatedFrom) p.set('updatedFrom', scope.updatedFrom);
    if (scope.updatedTo) p.set('updatedTo', scope.updatedTo);
    return get<{ items: TicketSummary[] }>(`/search/tickets?${p}`);
  },

  // Audit log
  listAuditEntries: (companyId: string, params: { action?: string; actor?: string; from?: string; to?: string; cursor?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, String(v));
    return get<{
      items: Array<{
        id: string; actorId: string; actorType: 'user' | 'mcp_client';
        action: string; resourceType: string; resourceId: string;
        ipAddress?: string; userAgent?: string;
        metadata?: Record<string, unknown>; occurredAt: string;
      }>;
      nextCursor?: string;
    }>(`/companies/${companyId}/audit${q.toString() ? `?${q}` : ''}`);
  },
  listTokens: () => get<ApiToken[]>('/me/tokens'),
  createToken: (data: {
    label: string;
    scope?: { companies?: string[]; projects?: string[]; readOnly?: boolean; mcp?: string[] };
    expiresInDays?: number | null;
  }) => post<{ token: ApiToken; secret: string }>('/me/tokens', data),
  revokeToken: (tokenId: string) => request<void>(`/me/tokens/${tokenId}`, { method: 'DELETE' }),

  // Local-only prefs (v1). Backend endpoint stubs exist in the PRD; wire later.
  // The view choices (sort, view, thumbnails, sidebar) are kept per project in
  // this browser across visits (board ticket t-hMetLW); filters are not.
  getPrefs(projectId: string): ProjectViewPrefs {
    return prefsStore[projectId]
      ?? (prefsStore[projectId] = { ...defaultPrefs(), ...readRememberedViewPrefs(projectId) });
  },
  setPrefs(projectId: string, prefs: Partial<ProjectViewPrefs>) {
    const next = { ...(prefsStore[projectId] ?? api.getPrefs(projectId)), ...prefs };
    prefsStore[projectId] = next;
    rememberViewPrefs(projectId, next);
  },

  // Live events — returns an unsubscribe function. Implementation
  // lives in `liveBus.ts`; api.ts re-exports it under the same shape
  // so callers don't need to know about the bus module directly.
  subscribe(listener: (e: LiveEvent) => void): () => void {
    return liveSubscribe(listener);
  },
};

export type Api = typeof api;

/** A project's Jira auto-send setting (routes/jira.ts `automationView`). */
export interface JiraAutomation {
  enabledAt: string;
  includeWidgetReports: boolean;
  ticketTypeIds: string[];
  afterSendColumnId: string | null;
  archiveAfterSend: boolean;
}
